// src/components/Settings/Pages/DocumentManagement.tsx
import React from "react";
import {
  Box,
  Chip,
  Typography,
  LinearProgress,
  Button,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import { Buffer } from "buffer";
import UploadDocumentDialog from "../Dialogs/UploadDocumentDialog";
import useDocumentManagement from "../../../hooks/useDocumentManagement";
import DocumentViewerDialog from "../../Dialogs/DocumentViewerDialog";

import TopicOutlinedIcon from "@mui/icons-material/TopicOutlined";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";

// Buffer polyfill setup
if (typeof window !== "undefined") {
  window.Buffer = window.Buffer || Buffer;
}

interface DocumentManagementProps {
  containerName?: string;
}

const DocumentManagement: React.FC<DocumentManagementProps> = ({
  containerName = "copilotdms",
}) => {
  const theme = useTheme();
  const {
    loading,
    documents,
    error,
    uploadOpen,
    selectedFile,
    uploadProgress,
    viewerOpen,
    selectedDocument,
    documentUrl,
    snackbar,
    totalRowCount,
    paginationModel,
    setUploadOpen,
    setSelectedFile,
    setViewerOpen,
    setSnackbar,
    handleViewDocument,
    handleDelete,
    handleFileUpload,
    handlePaginationModelChange,
  } = useDocumentManagement(containerName);

  const getFileIcon = (type: string) => {
    switch (type.toUpperCase()) {
      case "PDF":
        return (
          <PictureAsPdfIcon
            sx={{ fontSize: 16, color: theme.palette.error.main }}
          />
        );
      case "DOCX":
      case "DOC":
        return (
          <DescriptionIcon
            sx={{ fontSize: 16, color: theme.palette.primary.main }}
          />
        );
      default:
        return (
          <InsertDriveFileIcon
            sx={{ fontSize: 16, color: theme.palette.grey[500] }}
          />
        );
    }
  };

  const renderDocumentCell = (params: GridRenderCellParams) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      {getFileIcon(params.row.documentType)}
      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          width: "100%",
        }}
      >
        {params.value}
      </span>
    </Box>
  );

  const renderStatusCell = (params: GridRenderCellParams) => {
    let color: string;
    switch (params.value) {
      case "Active":
        color = theme.palette.success.main;
        break;
      case "Draft":
        color = theme.palette.warning.main;
        break;
      case "Archived":
        color = theme.palette.grey[500];
        break;
      default:
        color = theme.palette.grey[500];
    }
    return (
      <Chip
        label={params.value}
        sx={{
          backgroundColor: `${color}12`,
          color: color,
          borderRadius: 1,
          fontSize: 12,
        }}
      />
    );
  };

  const renderActionCell = (params: GridRenderCellParams) => (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        height: "100%",
        alignItems: "center",
      }}
    >
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          handleViewDocument(params.row);
        }}
        color="primary"
      >
        <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          handleDelete(params.row);
        }}
        color="error"
      >
        <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );

  const columns: GridColDef[] = [
    {
      field: "documentName",
      headerName: "Document Name",
      width: 320,
      renderCell: renderDocumentCell,
    },
    { field: "documentType", headerName: "Type", width: 120 },
    { field: "owner", headerName: "Owner", width: 180 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: renderStatusCell,
    },
    { field: "lastModified", headerName: "Last Modified", width: 180 },
    { field: "version", headerName: "Version", width: 120 },
    { field: "size", headerName: "Size", width: 120 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: renderActionCell,
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "column", md: "row" },
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          mb: 4,
          gap: 2,
        }}
      >
        <TopicOutlinedIcon color="warning" sx={{ fontSize: 32 }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1, // This ensures the content can grow and take available space
            textAlign: { xs: "center", sm: "center", md: "left" }, // Align text to the center on mobile, left on larger screens
          }}
        >
          <Typography
            variant="body1"
            color="textPrimary"
            sx={{ fontWeight: 600 }}
          >
            ข้อมูลเอกสาร
          </Typography>
          <Typography variant="body2" color="textSecondary">
            อัพโหลด แก้ไข และจัดการเอกสาร
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<CloudUploadOutlinedIcon />}
          onClick={() => setUploadOpen(true)}
        >
          อัพโหลดเอกสารใหม่
        </Button>
      </Box>
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              p: 4,
              gap: 2,
            }}
          >
            <TableViewOutlinedIcon
              sx={{ fontSize: 24, color: theme.palette.text.secondary }}
            />
            <LinearProgress
              color="primary"
              sx={{ width: "200px", borderRadius: "4px" }}
            />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ p: 4 }}>
            {error}
          </Typography>
        ) : (
          <DataGrid
            rows={documents}
            columns={columns}
            rowSelection={false}
            rowCount={totalRowCount}
            pageSizeOptions={[10, 25, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            loading={loading}
            disableColumnFilter
            autoHeight
          />
        )}
      </Box>

      {/* Upload Dialog */}
      <UploadDocumentDialog
        uploadOpen={uploadOpen}
        setUploadOpen={setUploadOpen}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        uploadProgress={uploadProgress}
        handleFileUpload={handleFileUpload}
        theme={theme}
      />

      {/* Document Viewer Dialog */}
      <DocumentViewerDialog
        viewerOpen={viewerOpen}
        setViewerOpen={setViewerOpen}
        name={selectedDocument?.documentName}
        type={selectedDocument?.documentType}
        url={documentUrl}
        theme={theme}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentManagement;
